import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { 
  AlertCircle, 
  ChevronRight, 
  Copy, 
  Filter,
  RefreshCw,
  Clock,
  ExternalLink,
  XCircle,
  AlertTriangle,
  Info,
  CheckCircle,
  Search,
  ChevronDown,
  Bug
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '../components/ui/sheet';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../components/ui/collapsible';
import { ScrollArea } from '../components/ui/scroll-area';
import { Separator } from '../components/ui/separator';
import { apiClient } from '../lib/api';

// Error severity mapping
const getErrorSeverity = (error) => {
  if (error.error_code === 'INTERNAL_ERROR') return 'critical';
  if (error.is_actionable && !error.transient_hint) return 'high';
  if (error.transient_hint) return 'medium';
  return 'low';
};

const severityConfig = {
  critical: { color: 'bg-red-500', icon: XCircle, label: 'Critique' },
  high: { color: 'bg-orange-500', icon: AlertTriangle, label: 'Élevé' },
  medium: { color: 'bg-yellow-500', icon: AlertCircle, label: 'Moyen' },
  low: { color: 'bg-blue-500', icon: Info, label: 'Faible' }
};

// Format relative time
const formatRelativeTime = (isoString) => {
  const date = new Date(isoString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  
  if (diffMins < 1) return 'À l\'instant';
  if (diffMins < 60) return `Il y a ${diffMins} min`;
  if (diffHours < 24) return `Il y a ${diffHours}h`;
  return `Il y a ${diffDays}j`;
};

// Error card component
const ErrorCard = ({ error, onClick }) => {
  const severity = getErrorSeverity(error);
  const { color, icon: Icon, label } = severityConfig[severity];
  
  return (
    <Card 
      className="cursor-pointer hover:shadow-md transition-shadow"
      onClick={() => onClick(error)}
      data-testid={`error-card-${error.request_id}`}
    >
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className={`p-2 rounded-full ${color} bg-opacity-20`}>
            <Icon className={`w-4 h-4 ${color.replace('bg-', 'text-')}`} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <Badge variant="outline" className="text-xs font-mono">
                {error.error_code}
              </Badge>
              <Badge variant="secondary" className="text-xs">
                {error.stage}
              </Badge>
              {(error.transient_hint || error.is_transient) && (
                <Badge 
                  variant="secondary" 
                  className="text-xs bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300"
                  data-testid="transient-badge"
                >
                  ⏳ transient
                </Badge>
              )}
              {error.auto_retry_allowed === true && (
                <Badge variant="secondary" className="text-xs bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                  auto-retry
                </Badge>
              )}
              {error.auto_retry_allowed === false && error.blocking_reason && (
                <Badge variant="secondary" className="text-xs bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300">
                  no-retry
                </Badge>
              )}
            </div>
            <p className="text-sm text-muted-foreground line-clamp-2 mb-2">
              {error.message}
            </p>
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {formatRelativeTime(error.created_at)}
              </span>
              <span className="font-mono text-[10px]">
                {error.request_id?.slice(0, 16)}...
              </span>
            </div>
          </div>
          <ChevronRight className="w-5 h-5 text-muted-foreground" />
        </div>
      </CardContent>
    </Card>
  );
};

// Error detail panel
const ErrorDetailPanel = ({ error, onClose, isMobile }) => {
  const [bundle, setBundle] = useState(null);
  const [loadingBundle, setLoadingBundle] = useState(false);
  const [copiedField, setCopiedField] = useState(null);
  
  const copyToClipboard = async (text, fieldName) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(fieldName);
      toast.success('Copié !');
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      toast.error('Erreur de copie');
    }
  };
  
  const loadBundle = async () => {
    if (!error?.request_id) return;
    setLoadingBundle(true);
    try {
      const response = await apiClient.get(`/support/diagnostics/${error.request_id}/bundle`);
      setBundle(response.data.bundle);
    } catch (err) {
      toast.error('Erreur lors du chargement du bundle');
    } finally {
      setLoadingBundle(false);
    }
  };
  
  const copyBundle = async () => {
    if (!bundle) {
      await loadBundle();
    }
    const bundleText = JSON.stringify(bundle || error, null, 2);
    copyToClipboard(bundleText, 'bundle');
  };
  
  if (!error) return null;
  
  const severity = getErrorSeverity(error);
  const { color, icon: Icon, label } = severityConfig[severity];
  
  const content = (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start gap-3">
        <div className={`p-3 rounded-full ${color} bg-opacity-20`}>
          <Icon className={`w-6 h-6 ${color.replace('bg-', 'text-')}`} />
        </div>
        <div className="flex-1">
          <h3 className="font-semibold text-lg">{error.error_code}</h3>
          <p className="text-muted-foreground text-sm">{error.message}</p>
        </div>
      </div>
      
      {/* Quick Info */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label className="text-xs text-muted-foreground">Request ID</Label>
          <div className="flex items-center gap-2">
            <code className="text-xs bg-muted px-2 py-1 rounded break-all">
              {error.request_id}
            </code>
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-6 w-6"
              onClick={() => copyToClipboard(error.request_id, 'request_id')}
            >
              {copiedField === 'request_id' ? <CheckCircle className="w-3 h-3 text-green-500" /> : <Copy className="w-3 h-3" />}
            </Button>
          </div>
        </div>
        <div>
          <Label className="text-xs text-muted-foreground">Stack Hash</Label>
          <code className="text-xs bg-muted px-2 py-1 rounded block">
            {error.stack_hash || 'N/A'}
          </code>
        </div>
        <div>
          <Label className="text-xs text-muted-foreground">Stage</Label>
          <Badge variant="outline">{error.stage}</Badge>
        </div>
        <div>
          <Label className="text-xs text-muted-foreground">HTTP Status</Label>
          <Badge variant={error.http_status >= 500 ? 'destructive' : 'secondary'}>
            {error.http_status}
          </Badge>
        </div>
        <div>
          <Label className="text-xs text-muted-foreground">Timestamp</Label>
          <p className="text-sm">{new Date(error.created_at).toLocaleString('fr-FR')}</p>
        </div>
        <div>
          <Label className="text-xs text-muted-foreground">Endpoint</Label>
          <code className="text-xs bg-muted px-2 py-1 rounded block truncate">
            {error.method} {error.path}
          </code>
        </div>
      </div>
      
      <Separator />
      
      {/* Flags */}
      <div>
        <Label className="text-xs text-muted-foreground mb-2 block">Flags</Label>
        <div className="flex gap-2 flex-wrap">
          <Badge variant={error.is_actionable ? 'default' : 'secondary'}>
            {error.is_actionable ? 'Actionnable' : 'Non-actionnable'}
          </Badge>
          {error.transient_hint && (
            <Badge variant="secondary" className="bg-yellow-100 text-yellow-800">
              Erreur temporaire
            </Badge>
          )}
          {error.retry_after_ms && (
            <Badge variant="outline">
              Retry: {error.retry_after_ms}ms
            </Badge>
          )}
        </div>
      </div>
      
      {/* Error Reason */}
      {error.error_reason && (
        <div>
          <Label className="text-xs text-muted-foreground mb-2 block">Raison</Label>
          <code className="text-sm bg-muted px-3 py-2 rounded block">
            {error.error_reason}
          </code>
        </div>
      )}
      
      {/* Meta Error */}
      {error.meta_error && (
        <Collapsible>
          <CollapsibleTrigger asChild>
            <Button variant="ghost" className="w-full justify-between">
              <span className="flex items-center gap-2">
                <ExternalLink className="w-4 h-4" />
                Erreur Meta API
              </span>
              <ChevronDown className="w-4 h-4" />
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent className="mt-2">
            <div className="bg-muted rounded-lg p-3 space-y-2 text-sm">
              {error.meta_error.meta_error_code && (
                <p><span className="text-muted-foreground">Code:</span> {error.meta_error.meta_error_code}</p>
              )}
              {error.meta_error.meta_error_subcode && (
                <p><span className="text-muted-foreground">Subcode:</span> {error.meta_error.meta_error_subcode}</p>
              )}
              {error.meta_error.meta_error_message && (
                <p><span className="text-muted-foreground">Message:</span> {error.meta_error.meta_error_message}</p>
              )}
              {error.meta_error.meta_trace_id && (
                <p><span className="text-muted-foreground">Trace ID:</span> <code>{error.meta_error.meta_trace_id}</code></p>
              )}
            </div>
          </CollapsibleContent>
        </Collapsible>
      )}
      
      {/* Debug Info */}
      {error.debug_info_sanitized && Object.keys(error.debug_info_sanitized).length > 0 && (
        <Collapsible>
          <CollapsibleTrigger asChild>
            <Button variant="ghost" className="w-full justify-between">
              <span className="flex items-center gap-2">
                <Bug className="w-4 h-4" />
                Informations de debug
              </span>
              <ChevronDown className="w-4 h-4" />
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent className="mt-2">
            <ScrollArea className="h-[200px]">
              <pre className="text-xs bg-muted p-3 rounded-lg overflow-x-auto">
                {JSON.stringify(error.debug_info_sanitized, null, 2)}
              </pre>
            </ScrollArea>
          </CollapsibleContent>
        </Collapsible>
      )}
      
      {/* Next Steps */}
      {error.next_steps && error.next_steps.length > 0 && (
        <div>
          <Label className="text-xs text-muted-foreground mb-2 block">Actions recommandées</Label>
          <ul className="space-y-2">
            {error.next_steps.map((step, i) => (
              <li key={i} className="flex items-start gap-2 text-sm">
                <span className="text-muted-foreground">{i + 1}.</span>
                {step}
              </li>
            ))}
          </ul>
        </div>
      )}
      
      {/* GARDE-FOU #6: Deployment Traceability */}
      {(error.build_sha || error.app_version || error.instance_id) && (
        <Collapsible>
          <CollapsibleTrigger asChild>
            <Button variant="ghost" className="w-full justify-between">
              <span className="flex items-center gap-2">
                <Info className="w-4 h-4" />
                Traçabilité déploiement
              </span>
              <ChevronDown className="w-4 h-4" />
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent className="mt-2">
            <div className="bg-muted rounded-lg p-3 space-y-2 text-sm">
              {error.app_version && (
                <p><span className="text-muted-foreground">Version:</span> {error.app_version}</p>
              )}
              {error.build_sha && (
                <p><span className="text-muted-foreground">Build SHA:</span> <code>{error.build_sha}</code></p>
              )}
              {error.build_time && (
                <p><span className="text-muted-foreground">Build Time:</span> {new Date(error.build_time).toLocaleString('fr-FR')}</p>
              )}
              {error.instance_id && (
                <p><span className="text-muted-foreground">Instance:</span> <code>{error.instance_id}</code></p>
              )}
            </div>
          </CollapsibleContent>
        </Collapsible>
      )}
      
      <Separator />
      
      {/* Copy Bundle Button */}
      <Button 
        onClick={copyBundle}
        disabled={loadingBundle}
        className="w-full"
        data-testid="copy-bundle-btn"
      >
        {loadingBundle ? (
          <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
        ) : copiedField === 'bundle' ? (
          <CheckCircle className="w-4 h-4 mr-2 text-green-500" />
        ) : (
          <Copy className="w-4 h-4 mr-2" />
        )}
        Copier diagnostic (support)
      </Button>
    </div>
  );
  
  if (isMobile) {
    return (
      <Dialog open={!!error} onOpenChange={() => onClose()}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Détail du diagnostic</DialogTitle>
            <DialogDescription>
              Informations détaillées sur cette erreur
            </DialogDescription>
          </DialogHeader>
          {content}
        </DialogContent>
      </Dialog>
    );
  }
  
  return (
    <Sheet open={!!error} onOpenChange={() => onClose()}>
      <SheetContent className="w-[450px] sm:w-[540px] overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Détail du diagnostic</SheetTitle>
          <SheetDescription>
            Informations détaillées sur cette erreur
          </SheetDescription>
        </SheetHeader>
        <div className="mt-6">
          {content}
        </div>
      </SheetContent>
    </Sheet>
  );
};

// Main page component
const SupportDiagnostics = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(false);
  const [nextCursor, setNextCursor] = useState(null);
  const [selectedError, setSelectedError] = useState(null);
  const [summary, setSummary] = useState(null);
  
  // Filters
  const [projectId, setProjectId] = useState(searchParams.get('project_id') || '');
  const [errorCode, setErrorCode] = useState(searchParams.get('error_code') || '');
  const [stage, setStage] = useState(searchParams.get('stage') || '');
  const [since, setSince] = useState(searchParams.get('since') || '24h');
  
  // Mobile detection
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Load errors
  const loadErrors = useCallback(async (cursor = null) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (projectId) params.append('project_id', projectId);
      if (errorCode) params.append('error_code', errorCode);
      if (stage) params.append('stage', stage);
      params.append('since', since);
      params.append('limit', '20');
      if (cursor) params.append('cursor', cursor);
      
      const response = await apiClient.get(`/support/diagnostics?${params.toString()}`);
      const data = response.data.data;
      
      if (cursor) {
        setEvents(prev => [...prev, ...data.events]);
      } else {
        setEvents(data.events);
      }
      setHasMore(data.has_more);
      setNextCursor(data.next_cursor);
      
    } catch (err) {
      toast.error('Erreur lors du chargement des diagnostics');
    } finally {
      setLoading(false);
    }
  }, [projectId, errorCode, stage, since]);
  
  // Load summary
  const loadSummary = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (projectId) params.append('project_id', projectId);
      
      const response = await apiClient.get(`/support/diagnostics/summary?${params.toString()}`);
      setSummary(response.data.data);
    } catch (err) {
      console.error('Failed to load summary:', err);
    }
  }, [projectId]);
  
  // Initial load
  useEffect(() => {
    loadErrors();
    loadSummary();
  }, [loadErrors, loadSummary]);
  
  // GARDE-FOU #5: Handle URL param to open specific error immediately
  useEffect(() => {
    const openId = searchParams.get('open');
    if (!openId) return;
    
    // Try to find in loaded events first
    const error = events.find(e => e.request_id === openId);
    if (error) {
      setSelectedError(error);
      return;
    }
    
    // Load specific error directly (don't wait for events list)
    if (events.length === 0 || !events.find(e => e.request_id === openId)) {
      apiClient.get(`/support/diagnostics/${openId}`)
        .then(res => {
          if (res.data.data) {
            setSelectedError(res.data.data);
          }
        })
        .catch((err) => {
          console.error('Failed to load diagnostic:', err);
          toast.error('Diagnostic non trouvé ou accès refusé');
        });
    }
  }, [searchParams, events]);
  
  // Apply filters
  const applyFilters = () => {
    const params = new URLSearchParams();
    if (projectId) params.set('project_id', projectId);
    if (errorCode) params.set('error_code', errorCode);
    if (stage) params.set('stage', stage);
    params.set('since', since);
    setSearchParams(params);
    loadErrors();
  };
  
  return (
    <div className="container mx-auto p-4 max-w-4xl" data-testid="support-diagnostics-page">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Bug className="w-6 h-6" />
          Diagnostics
        </h1>
        <p className="text-muted-foreground mt-1">
          Consultez vos erreurs récentes et obtenez des informations de debug
        </p>
      </div>
      
      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold">{summary.total_24h}</p>
              <p className="text-xs text-muted-foreground">Erreurs (24h)</p>
            </CardContent>
          </Card>
          {Object.entries(summary.by_error_code || {}).slice(0, 3).map(([code, count]) => (
            <Card key={code}>
              <CardContent className="p-4 text-center">
                <p className="text-xl font-bold">{count}</p>
                <p className="text-xs text-muted-foreground truncate">{code}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
      
      {/* Filters */}
      <Card className="mb-6">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <Filter className="w-4 h-4" />
            Filtres
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <Label className="text-xs">Période</Label>
              <Select value={since} onValueChange={setSince}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1h">Dernière heure</SelectItem>
                  <SelectItem value="24h">24 heures</SelectItem>
                  <SelectItem value="7d">7 jours</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Code erreur</Label>
              <Input 
                placeholder="Ex: INTERNAL_ERROR"
                value={errorCode}
                onChange={(e) => setErrorCode(e.target.value)}
              />
            </div>
            <div>
              <Label className="text-xs">Stage</Label>
              <Input 
                placeholder="Ex: MIDDLEWARE"
                value={stage}
                onChange={(e) => setStage(e.target.value)}
              />
            </div>
            <div className="flex items-end">
              <Button onClick={applyFilters} className="w-full">
                <Search className="w-4 h-4 mr-2" />
                Filtrer
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Error List */}
      <div className="space-y-3">
        {loading && events.length === 0 ? (
          <div className="text-center py-12">
            <RefreshCw className="w-8 h-8 animate-spin mx-auto text-muted-foreground" />
            <p className="mt-2 text-muted-foreground">Chargement...</p>
          </div>
        ) : events.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <CheckCircle className="w-12 h-12 mx-auto text-green-500 mb-4" />
              <h3 className="font-semibold text-lg">Aucune erreur</h3>
              <p className="text-muted-foreground">
                Aucune erreur trouvée pour la période sélectionnée
              </p>
            </CardContent>
          </Card>
        ) : (
          <>
            {events.map((event) => (
              <ErrorCard 
                key={event.request_id} 
                error={event}
                onClick={setSelectedError}
              />
            ))}
            
            {hasMore && (
              <Button 
                variant="outline" 
                className="w-full"
                onClick={() => loadErrors(nextCursor)}
                disabled={loading}
              >
                {loading ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : null}
                Charger plus
              </Button>
            )}
          </>
        )}
      </div>
      
      {/* Error Detail Panel */}
      <ErrorDetailPanel 
        error={selectedError}
        onClose={() => {
          setSelectedError(null);
          // Remove 'open' param from URL
          const params = new URLSearchParams(searchParams);
          params.delete('open');
          setSearchParams(params);
        }}
        isMobile={isMobile}
      />
    </div>
  );
};

export default SupportDiagnostics;
